import React, { useState, useEffect, useRef } from 'react';
import { api } from '../api';
import { trackCertificateActivated } from '../utils/metrika';

function CertificateActivateForm({ defaultCode = '', onActivated, inline = false }) {
  const [code, setCode] = useState((defaultCode || '').toUpperCase());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (defaultCode) {
      setCode(String(defaultCode).toUpperCase());
      setTimeout(() => {
        try {
          containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } catch {}
      }, 100);
    }
  }, [defaultCode]);

  const handleSubmit = async () => {
    setError('');
    setSuccess(null);
    const trimmed = code.trim();
    if (!trimmed) {
      setError('Введите код сертификата');
      return;
    }
    setIsLoading(true);
    try {
      const result = await api.activateCertificate(trimmed);
      trackCertificateActivated(result.amount);
      setSuccess(result);
      setCode('');
      // Помечаем код использованным: start_param `cert_<код>` живёт всю сессию
      // мини-аппа, и профиль подставлял его заново при каждом возврате — юзер
      // жал «активировать» и получал «сертификат уже использован» (жалоба 05.09).
      try {
        localStorage.setItem('stems_cert_activated_code', trimmed.toUpperCase());
      } catch (e) {}
      onActivated?.(result);
    } catch (e) {
      // Логируем для диагностики если ошибка пришла без понятного текста
      if (!e?.message || !e.message.trim()) {
        console.error('Cert activate failed without message:', e);
      }
      const msg = (e?.message || '').trim();
      setError(msg || 'Не удалось активировать сертификат. Попробуйте позже.');
    } finally {
      setIsLoading(false);
    }
  };

  const wrapperStyle = inline
    ? { padding: '4px 0' }
    : { background: '#F5F5F5', borderRadius: 12, padding: 16, margin: '12px 0' };

  return (
    <div ref={containerRef} style={wrapperStyle}>
      <div style={{
        fontSize: 14,
        fontWeight: 500,
        color: 'var(--text-secondary, #666)',
        marginBottom: 10,
      }}>
        Активировать сертификат
      </div>
      {success && (
        <div style={{
          background: '#E8F5E9',
          color: '#2E7D32',
          borderRadius: 8,
          padding: '10px 12px',
          marginBottom: 10,
          fontSize: 14,
        }}>
          Зачислено <b>{success.amount.toLocaleString('ru-RU')} ₽</b> на ваш счёт
        </div>
      )}
      {error && (
        <div style={{
          background: '#FFEBEE',
          color: '#C62828',
          borderRadius: 8,
          padding: '10px 12px',
          marginBottom: 10,
          fontSize: 14,
        }}>
          {error}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase().replace(/\s/g, ''))}
          placeholder="Код сертификата"
          maxLength={20}
          style={{
            flex: 1,
            minWidth: 0,
            boxSizing: 'border-box',
            padding: '12px 14px',
            border: '1px solid #DDD',
            borderRadius: 10,
            fontSize: 16,
            letterSpacing: 1,
            background: '#FFF',
          }}
        />
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isLoading || !code.trim()}
          style={{
            flexShrink: 0,
            minHeight: 44,
            padding: '0 18px',
            background: isLoading || !code.trim() ? '#E5E5E5' : 'var(--text-color, #1A1A1A)',
            color: isLoading || !code.trim() ? '#999' : 'var(--primary-color)',
            border: 'none',
            borderRadius: 10,
            fontWeight: 600,
            fontSize: 14,
            cursor: isLoading || !code.trim() ? 'not-allowed' : 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          {isLoading ? '...' : 'Применить'}
        </button>
      </div>
    </div>
  );
}

export default CertificateActivateForm;
